<template>
  <v-dialog
    v-model="visible"
    persistent
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <!-- <v-card style="height: calc(100vh - 64px)"> -->
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="onClose">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ `Skapa ny bild` }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="onLock" :disabled="!previewImage">
          <v-icon>{{ isLockedToStandard ? 'mdi-lock-outline' : 'mdi-lock-open-outline' }}</v-icon>
          {{ isLockedToStandard ? 'Lås upp' : 'Lås' }}
        </v-btn>
      </v-toolbar-items>
      <v-toolbar-items>
        <v-btn dark text @click="onSave" :loading="loading"> Spara </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <div class="image-preview-container">
      <Upload
        v-if="!previewImage"
        @add="onAddFile"
        container="bilder"
        accept="image/jpeg"
        class="my-3 mb-5"
      >
        <div class="upload-container">
          <template>
            <div class="upload-message">
              <v-btn icon fab color="primary">
                <v-icon dark size="xx-large"> mdi-cloud-upload-outline </v-icon>
              </v-btn>
              <h4 style="text-align: center">Klicka eller dra bild hit för att ladda upp bild.</h4>
            </div>
          </template>
        </div>
      </Upload>
      <div class="preview-image mt-3 mb-5" v-if="previewImage">
        <cropper
          ref="cropper"
          class="cropper"
          :src="previewImage.src"
          :default-size="cropperDefaultSize"
          :stencil-props="stencilProps"
          default-boundaries="fill"
        />
      </div>
    </div>
  </v-dialog>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import { mapActions } from 'vuex'
import Upload from '@/components/shared/Upload.vue'
import { getImageDimensions, getFileExtension } from '@/lib/misc'
import { resizeImage, imageResizeConfigs, dataUrlToFile } from '@/lib/Image'
import AzureStorage from '../../lib/AzureBlobStorage'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

const CONTAINER_IMAGES = 'bilder'
const MIN_WIDTH = 1920

export default {
  name: 'ImageCreate',
  components: {
    Upload,
    Cropper,
  },
  data() {
    return {
      visible: true,
      title: 'Namnlös',
      loading: false,
      uploadFile: null,
      previewImage: null,
      resizeImageQueue: [],
      id: null,
      isLockedToStandard: true,
    }
  },
  computed: {
    imageHasMinimumWidth() {
      return this.previewImage.width >= MIN_WIDTH
    },
    stencilProps() {
      return {
        aspectRatio: this.isLockedToStandard ? 16 / 9 : null,
        resizable: !this.isLockedToStandard,
        handlers: {
          eastNorth: !this.isLockedToStandard,
          north: !this.isLockedToStandard,
          westNorth: !this.isLockedToStandard,
          west: !this.isLockedToStandard,
          westSouth: !this.isLockedToStandard,
          south: !this.isLockedToStandard,
          eastSouth: !this.isLockedToStandard,
          east: !this.isLockedToStandard,
        },
      }
    },
  },
  methods: {
    ...mapActions('image', { createImage: 'create', resizeImage: 'resizeImage' }),

    cropperDefaultSize({ imageSize, visibleArea }) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height,
      }
    },
    onLock() {
      this.isLockedToStandard = !this.isLockedToStandard
      this.$refs.cropper.reset()
    },
    onClose() {
      this.id = null
      this.previewImage = null
      this.$router.replace({ name: 'ImagesList' })
    },
    async onAddFile({ file }) {
      try {
        const imageInfo = {
          ...(await getImageDimensions(file)),
          src: URL.createObjectURL(file),
          file,
        }
        this.previewImage = imageInfo
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err)
      }
    },
    async onSave() {
      this.loading = true
      const payload = {
        image_publish: 'online',
        image_title: this.title,
      }
      const { id } = await this.createImage(payload)
      this.id = id
      await this.saveImage(id)
      this.loading = false
      this.$router.replace({ name: 'Image', params: { id: this.id } })
    },

    async saveImage(id) {
      const { file } = this.previewImage
      const newFilename = `${id}.${getFileExtension(file.name)}`

      const { canvas } = this.$refs.cropper.getResult()
      const resizedImageFile = await dataUrlToFile(canvas.toDataURL(), file.name)

      await Promise.all(
        imageResizeConfigs.map(async (config) => {
          const resizedImage = AzureStorage.createUpload(
            CONTAINER_IMAGES,
            await resizeImage(resizedImageFile, config),
            `${config.folder}/${newFilename}`
          )
          this.resizeImageQueue.push(resizedImage)
        })
      )
      this.resizeImageQueue.map(async (file) => await file.send())
    },
  },
}
</script>

<style lang="less" scoped>
.image-preview-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 20px;
  height: calc(100vh - 64px);
  background: #fff;
  padding: 0 20px;
  overflow: hidden;

  .upload-container {
    padding: 15px;
    display: flex;
    flex-direction: column;
    align-items: center;
    .upload-message {
      display: flex;
      justify-content: center;
      flex-direction: column;
      margin-top: 50px;
      align-items: center;
    }
  }
  .preview-image {
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    img {
      max-width: 100%;
      max-height: 100%;
    }
    // border: 2px solid #ececec;
    // padding: 5px;
    .info-row {
      display: flex;
      padding-top: 4px;
      align-items: center;
      justify-content: space-between;
      .size {
        display: inline-block;
        padding: 2px 5px;
      }
      .remove:hover {
        cursor: pointer;
        color: #000;
      }
    }
  }
}
</style>
